import { apiClient } from './client';

/**
 * 회사 일정 — 근태 4단계 (2026-08-24).
 *
 * 서버: `apps/chat-server/src/modules/hr/company-holiday.routes.ts`.
 * 설계: `docs/attendance-round2-sample.html` 제안 5 · 화면 K.
 *
 * 휴일은 **회사가 실제로 쉬는 날**이지 나라가 정한 목록이 아니다. 표시 기준은 회사 일정이고,
 * 시스템 목록은 회사가 아직 정하지 않은 날을 채우는 바탕이다.
 */

export type HolidayKind = 'ADD' | 'EXCLUDE' | 'OVERRIDE';

export const HOLIDAY_KIND_LABEL: Record<HolidayKind, string> = {
  ADD: '회사 휴일',
  EXCLUDE: '안 쉼',
  OVERRIDE: '이름·유급 덮기',
};

export interface EffectiveHolidayDto {
  date: string;
  name: string;
  isPaid: boolean;
  source: 'COMPANY' | 'SYSTEM';
  kind: HolidayKind | null;
  isSubstitute: boolean;
}

export interface SystemHolidayDto { id: string; date: string; name: string; isSubstitute: boolean }
export interface CompanyHolidayDto {
  id: string; date: string; name: string; kind: HolidayKind; isPaid: boolean; sourceHolidayId: string | null;
  /**
   * 휴일대체 (2026-08-30 · 판 K) — 이 줄이 **어느 날 대신**인가. 짝의 날짜다.
   * ★ 옛 서버는 이 칸을 안 준다 → 없으면 대체가 아닌 보통 줄로 본다.
   */
  substituteForDate?: string | null;
  /** 근기법 §55② 서면 합의 근거 메모. */
  substituteNote?: string | null;
}

/**
 * 합친 결과 — 전 직원이 읽는다. 캘린더·근태가 **같은 함수의 결과**를 쓴다.
 *
 * ★ 옛 서버에는 이 길이 없다(404). 그때는 호출부가 전역 목록으로 물러난다 — 프론트를 먼저
 *   배포해도 화면이 비지 않게 하려는 것이다(배포 순서 결합 제거).
 */
export async function listEffectiveHolidays(year: number, country = 'KR'): Promise<EffectiveHolidayDto[]> {
  const { data } = await apiClient.get<{ holidays: EffectiveHolidayDto[] }>('/holidays/effective', {
    params: { year, country },
  });
  return Array.isArray(data?.holidays) ? data.holidays : [];
}

/** 관리 — 두 층과 합친 결과를 함께 받는다. */
export async function listCompanyHolidays(year: number, country = 'KR'): Promise<{
  year: number; country: string;
  system: SystemHolidayDto[]; company: CompanyHolidayDto[]; effective: EffectiveHolidayDto[];
}> {
  const { data } = await apiClient.get('/hr/company-holidays', { params: { year, country } });
  return data;
}

/** 관리 — 회사 줄 추가·갱신. `EXCLUDE` 는 유급 여부를 받지 않는다(그날은 휴일이 아니다). */
export async function saveCompanyHoliday(body: {
  date: string; name: string; kind: HolidayKind; isPaid?: boolean; sourceHolidayId?: string | null;
}): Promise<{ id: string }> {
  const { data } = await apiClient.post('/hr/company-holidays', body);
  return data;
}

/** 관리 — 회사 줄 삭제. **시스템 목록은 손대지 않는다.** */
export async function deleteCompanyHoliday(id: string): Promise<void> {
  await apiClient.delete(`/hr/company-holidays/${id}`);
}

/**
 * **휴일대체** — 두 줄을 한 번에 만든다 (2026-08-30 · 판 K).
 *
 * ★ 손으로 EXCLUDE·ADD 를 따로 만들게 하면 **하나만 만들고 마는 일**이 반드시 생긴다.
 *   그러면 휴일이 사라지거나 하루가 더 는다.
 */
export async function saveHolidaySubstitution(body: {
  fromDate: string; toDate: string; name?: string; note?: string;
}): Promise<{ fromDate: string; toDate: string; name: string }> {
  const { data } = await apiClient.post('/hr/holiday-substitutions', body);
  return data;
}

/** 되돌리기 — **두 줄을 함께** 지운다. */
export async function deleteHolidaySubstitution(fromDate: string, toDate: string): Promise<{ removed: number }> {
  const { data } = await apiClient.delete('/hr/holiday-substitutions', { params: { fromDate, toDate } });
  return data;
}
